import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import usePlatform from '@/utils/hooks/usePlatform';
import SignaturePad from '@/components/Forms/signature/SignaturePad';
import FieldLabel from './FieldLabel';
import { getFormEntryFileUrl } from '@/data/forms';
import {
    SIGNER_ROLE_LABELS,
    allowedSignerRoles,
    roleNeedsParticipant,
    consentTextForRole,
    toSignatureDraft,
} from '@/utils/formSignaturePolicy';

const ROLE_ICONS = {
    self: 'person-outline',
    parentGuardian: 'shield-checkmark-outline',
};

// requestAnimationFrame/setTimeout aren't guaranteed in headless or test
// environments — fall back to a synchronous flush so stroke buffering still
// works everywhere.
const scheduleFrame = (fn) => {
    if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(fn);
    if (typeof setTimeout === 'function') return setTimeout(fn, 16);
    fn();
    return null;
};
const cancelFrame = (id) => {
    if (id == null) return;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
    else if (typeof clearTimeout === 'function') clearTimeout(id);
};

/**
 * Read-only signature evidence — rendered for renderMode === 'submission'.
 * Value is the server-normalized object: `{ kind, signerName, signerRole,
 * participantName, consentAccepted, consentVersion, consentText,
 * agreementText, signedAt, image: { path, filename, signedUrl? } }`.
 * No pad, no checkboxes, no Clear.
 */
const SignatureSubmission = ({ field, value }) => {
    const [imageUri, setImageUri] = useState(value?.image?.signedUrl || null);
    const [imageError, setImageError] = useState(false);

    // Sign on demand when no URL was pre-resolved (short-lived, never persisted).
    useEffect(() => {
        let cancelled = false;
        if (!imageUri && value?.image?.path) {
            getFormEntryFileUrl({ path: value.image.path, inline: true })
                .then((url) => { if (!cancelled) setImageUri(url); })
                .catch(() => { if (!cancelled) setImageError(true); });
        }
        return () => { cancelled = true; };
    }, [value?.image?.path]);

    if (!value) return <Text style={styles.muted}>No signature recorded.</Text>;

    return (
        <View>
            <FieldLabel
                label={field.label}
                description={field.description}
                testID={`form-field-${field.fieldId}-label`}
            />
            {!!value.agreementText && (
                <View style={styles.agreement} testID={`signature-${field.fieldId}-agreement`}>
                    <Text style={styles.agreementText}>{value.agreementText}</Text>
                </View>
            )}
            <View style={styles.evidencePad} testID={`signature-${field.fieldId}-image`}>
                {imageUri ? (
                    <Image
                        source={{ uri: imageUri }}
                        style={styles.evidenceImage}
                        contentFit="contain"
                        accessibilityLabel={`Signature of ${value.signerName || 'signer'}`}
                    />
                ) : (
                    <Text style={styles.muted}>
                        {imageError ? 'Signature image unavailable.' : 'Loading signature…'}
                    </Text>
                )}
            </View>
            <Text style={styles.evidenceLine}>
                Signed by {value.signerName || '—'}
                {value.signerRole ? ` as ${SIGNER_ROLE_LABELS[value.signerRole] || value.signerRole}` : ''}
                {value.participantName ? ` for ${value.participantName}` : ''}
                {value.signedAt ? ` on ${new Date(value.signedAt).toLocaleString()}` : ''}
            </Text>
            {!!value.consentText && (
                <Text style={styles.consentEcho} testID={`signature-${field.fieldId}-consent-text`}>
                    {value.consentText}
                </Text>
            )}
        </View>
    );
};

/**
 * Signature field (fill/preview). The signer draws THEIR OWN signature; for
 * the parentGuardian capacity they sign on behalf of the named participant —
 * never imitating the participant's signature.
 *
 * Emits a normalized draft (`toSignatureDraft`) or null when untouched:
 * `{ kind:'signature', version:1, signerName, signerRole, participantName,
 *    consentAccepted, consentVersion, image:{base64,mimeType} }`.
 *
 * Consent reset rules: changing capacity/name/participant, new ink (including
 * strokes drawn after an export), Clear, and agreement/revision changes all
 * reset consentAccepted. See plan §4.2.
 */
const SignatureField = ({ field, value, onChange, disabled, renderMode = 'fill', formContext = {} }) => {
    const { isNative } = usePlatform();
    const padRef = useRef(null);
    const [strokes, setStrokes] = useState([]);
    const [exportError, setExportError] = useState(null);
    const [expandOpen, setExpandOpen] = useState(false);
    const exportGeneration = useRef(0);
    const strokesBufferRef = useRef(null);
    const strokeFrameRef = useRef(null);
    const agreementStamp = useRef(`${field.config?.agreementText || ''}|${formContext.contentRevision || ''}`);
    const fieldId = field.fieldId;

    const draft = value && typeof value === 'object' && value.kind === 'signature' ? value : null;
    const signerRole = draft?.signerRole || null;
    const signerName = draft?.signerName || '';
    const participantName = draft?.participantName || '';
    const consentAccepted = draft?.consentAccepted === true;
    const image = draft?.image || null;
    const roles = allowedSignerRoles(field);
    const agreementText = field.config?.agreementText || '';

    const emit = useCallback((patch) => {
        const next = {
            signerName, signerRole, participantName,
            consentAccepted, image, ...patch,
        };
        onChange?.(toSignatureDraft(next));
    }, [signerName, signerRole, participantName, consentAccepted, image, onChange]);

    const setPending = useCallback((pending) => {
        formContext.onSignaturePending?.(fieldId, pending);
    }, [formContext, fieldId]);

    // Clear pending export state when unmounted (repeat removal etc.).
    useEffect(() => () => {
        formContext.onSignaturePending?.(fieldId, false);
    }, [fieldId, formContext]);

    // Agreement text or accepted content revision changed mid-session → the
    // previously accepted consent no longer refers to the same terms.
    useEffect(() => {
        const stamp = `${field.config?.agreementText || ''}|${formContext.contentRevision || ''}`;
        if (stamp === agreementStamp.current) return;
        agreementStamp.current = stamp;
        if (consentAccepted) emit({ consentAccepted: false });
    }, [field.config?.agreementText, formContext.contentRevision, consentAccepted, emit]);

    // 409 form_changed: FormView cleared the draft value, but the pad's own
    // stroke state lives outside the payload. The epoch bump says "the terms
    // changed — discard ink too" so a stale drawing can't linger or resubmit.
    const epochRef = useRef(formContext.signatureResetEpoch ?? 0);
    useEffect(() => {
        const epoch = formContext.signatureResetEpoch ?? 0;
        if (epoch === epochRef.current) return;
        epochRef.current = epoch;
        exportGeneration.current += 1;
        padRef.current?.clear?.();
        strokesBufferRef.current = null;
        if (strokeFrameRef.current != null) {
            cancelFrame(strokeFrameRef.current);
            strokeFrameRef.current = null;
        }
        setStrokes([]);
        setExportError(null);
        setPending(false);
    }, [formContext.signatureResetEpoch, setPending]);

    const requestExport = useCallback(async () => {
        const generation = ++exportGeneration.current;
        setPending(true);
        try {
            const exported = await padRef.current?.exportPng?.();
            if (generation !== exportGeneration.current) return;
            if (!exported?.base64) throw new Error('empty export');
            setExportError(null);
            // New ink after consent → consent must be re-confirmed.
            emit({ image: { base64: exported.base64, mimeType: 'image/png' }, consentAccepted: false });
        } catch {
            if (generation === exportGeneration.current) {
                setExportError('Couldn\u2019t process the signature. Please draw it again.');
            }
        } finally {
            if (generation === exportGeneration.current) setPending(false);
        }
    }, [emit, setPending]);

    // The exported PNG is only the wire artifact — the pad itself keeps
    // showing the live ink, so drawing can continue after an export (e.g.
    // signing "First" then "Last"). A NEW stroke after an export invalidates
    // the stale artifact + consent once, then re-exports on finish.
    const exportInvalidatedRef = useRef(false);
    useEffect(() => {
        // A fresh export landed → the next stroke must invalidate it again.
        if (image) exportInvalidatedRef.current = false;
    }, [image]);

    // Stroke events fire per pointer move — buffer the latest frame's strokes
    // so React only re-renders once per animation frame, not per event. The
    // pad itself repaints from its own ref, so ink stays live.
    const flushStrokes = useCallback(() => {
        strokeFrameRef.current = null;
        if (strokesBufferRef.current != null) {
            setStrokes(strokesBufferRef.current);
            strokesBufferRef.current = null;
        }
    }, []);

    const handleStrokeChange = useCallback((nextStrokes, meta) => {
        if (meta?.finished) {
            // Flush synchronously — the export must see the final ink.
            if (strokeFrameRef.current != null) {
                cancelFrame(strokeFrameRef.current);
                strokeFrameRef.current = null;
            }
            strokesBufferRef.current = null;
            setStrokes(nextStrokes);
            if (!meta.isEmpty) {
                // Ink replaces any previously exported image and resets consent.
                requestExport();
            }
            return;
        }
        // First stroke event after a completed export — the new ink isn't in
        // that PNG, so drop it (and consent) right away instead of at finish.
        if (image && !exportInvalidatedRef.current) {
            exportInvalidatedRef.current = true;
            emit({ image: null, consentAccepted: false });
        }
        strokesBufferRef.current = nextStrokes;
        if (strokeFrameRef.current == null) strokeFrameRef.current = scheduleFrame(flushStrokes);
    }, [requestExport, flushStrokes, image, emit]);

    useEffect(() => () => {
        if (strokeFrameRef.current != null) cancelFrame(strokeFrameRef.current);
    }, []);

    const handleClear = useCallback(() => {
        exportGeneration.current += 1;
        padRef.current?.clear?.();
        strokesBufferRef.current = null;
        if (strokeFrameRef.current != null) {
            cancelFrame(strokeFrameRef.current);
            strokeFrameRef.current = null;
        }
        setStrokes([]);
        setExportError(null);
        setPending(false);
        emit({ image: null, consentAccepted: false });
    }, [emit, setPending]);

    if (renderMode === 'submission') {
        return <SignatureSubmission field={field} value={value} />;
    }

    // Single pad instance — while the expand sheet is open the inline pad is
    // unmounted so padRef always points at the visible one. Strokes are a
    // controlled prop, so they survive the remount. The pad stays ENABLED
    // after an export — continued drawing appends ink and re-exports.
    const pad = (
        <SignaturePad
            ref={padRef}
            strokes={strokes}
            onStrokeChange={handleStrokeChange}
            disabled={disabled}
            height={expandOpen ? 320 : 200}
            testID={`signature-${fieldId}-pad`}
        />
    );

    return (
        <View>
            <FieldLabel
                label={field.label}
                required={field.required}
                description={field.description}
                testID={`form-field-${fieldId}-label`}
            />

            {!!agreementText && (
                <View style={styles.agreement} testID={`signature-${fieldId}-agreement`}>
                    <Text style={styles.agreementText}>{agreementText}</Text>
                </View>
            )}

            <Text style={styles.subLabel}>
                Signing as<Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.roleRow} testID={`signature-${fieldId}-role`}>
                {roles.map((role) => {
                    const active = signerRole === role;
                    return (
                        <Pressable
                            key={role}
                            style={styles.roleChip(active)}
                            disabled={disabled}
                            onPress={() => {
                                // Capacity change resets consent (and participant
                                // name when switching back to self).
                                emit({
                                    signerRole: role,
                                    participantName: roleNeedsParticipant(role) ? participantName : null,
                                    consentAccepted: false,
                                });
                            }}
                            testID={`signature-${fieldId}-role-${role}`}
                            accessibilityRole="button"
                            accessibilityState={{ selected: active, disabled }}
                        >
                            <Unicon
                                name={ROLE_ICONS[role] || 'person-outline'}
                                size={14}
                                colorVariant={active ? 'onForms' : 'forms'}
                            />
                            <Text style={styles.roleChipText(active)}>{SIGNER_ROLE_LABELS[role]}</Text>
                        </Pressable>
                    );
                })}
            </View>

            <Input
                testID={`signature-${fieldId}-name`}
                title="Full legal name"
                placeholder="Actual signer\u2019s name"
                value={signerName}
                onUpdate={(text) => emit({ signerName: text, consentAccepted: false })}
                required
                disabled={disabled}
                autoCapitalize="words"
                autoComplete="name"
            />

            {roleNeedsParticipant(signerRole) && (
                <Input
                    testID={`signature-${fieldId}-participant`}
                    title="Participant name"
                    placeholder="Name of the person you are signing for"
                    value={participantName}
                    onUpdate={(text) => emit({ participantName: text, consentAccepted: false })}
                    required
                    disabled={disabled}
                    autoCapitalize="words"
                />
            )}

            <View style={styles.padFrame}>
                {/* The pad is never swapped for the exported PNG — it already
                    shows the same ink, and must keep accepting strokes. */}
                {!expandOpen ? pad : null}
                {!strokes.length && (
                    <Text style={styles.padPlaceholder} pointerEvents="none">Sign here</Text>
                )}
            </View>

            <View style={styles.padActions}>
                {!disabled && (
                    <Pressable onPress={handleClear} hitSlop={8} testID={`signature-${fieldId}-clear`}>
                        <Text style={styles.clearText}>Clear signature</Text>
                    </Pressable>
                )}
                {isNative && !disabled && (
                    <Pressable onPress={() => setExpandOpen(true)} hitSlop={8} testID={`signature-${fieldId}-expand`}>
                        <Text style={styles.clearText}>Expand signature</Text>
                    </Pressable>
                )}
            </View>
            {!!exportError && <Text style={styles.error}>{exportError}</Text>}

            {/* Consent wording depends on capacity — render only once one is
                chosen so the signer never confirms a template they can't see. */}
            {!!signerRole && (
                <View style={styles.consentRow}>
                    <Checkbox
                        testID={`signature-${fieldId}-consent`}
                        checked={consentAccepted}
                        disabled={disabled}
                        onChange={(next) => emit({ consentAccepted: next === true })}
                        label={consentTextForRole(signerRole)}
                    />
                </View>
            )}

            {expandOpen && (
                <SheetModal
                    isOpen={expandOpen}
                    title="Sign here"
                    cancelText="Done"
                    onCancel={() => setExpandOpen(false)}
                    onClose={() => setExpandOpen(false)}
                >
                    <View style={styles.expandPad}>{pad}</View>
                    <SolidButton
                        text="Clear signature"
                        onPress={handleClear}
                        variant="muted"
                        type="textSecondary"
                        size="sm"
                    />
                </SheetModal>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    agreement: {
        backgroundColor: colors.formsContainer,
        borderRadius: roundness.md,
        padding: spacing.sm,
        marginBottom: spacing.sm,
    },
    agreementText: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        lineHeight: typography.caption * 1.45,
    },
    subLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
        marginBottom: 6,
        marginLeft: 4,
    },
    required: {
        color: colors.error,
    },
    roleRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: spacing.sm,
    },
    roleChip: (active) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: active ? colors.forms : colors.border,
        backgroundColor: active ? colors.forms : colors.surface,
    }),
    roleChipText: (active) => ({
        // onForms pairs with colors.forms in both schemes — readable in dark
        // mode (dark text on bright teal) and light mode (white on teal).
        color: active ? colors.onForms : colors.text,
        fontFamily: fonts.heading,
        fontSize: 13,
    }),
    padFrame: {
        borderRadius: roundness.md,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        justifyContent: 'center',
    },
    padPlaceholder: {
        position: 'absolute',
        alignSelf: 'center',
        color: '#9ca3af',
        fontFamily: fonts.body,
        fontSize: typography.body,
    },
    padActions: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: spacing.md,
        paddingTop: 4,
    },
    clearText: {
        color: colors.forms,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    error: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        paddingTop: 4,
    },
    consentRow: {
        marginTop: spacing.sm,
    },
    expandPad: {
        paddingVertical: spacing.sm,
    },
    muted: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        padding: spacing.sm,
    },
    evidencePad: {
        borderRadius: roundness.md,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: '#ffffff',
        minHeight: 120,
        justifyContent: 'center',
        alignItems: 'center',
    },
    evidenceImage: {
        width: '100%',
        height: 160,
    },
    evidenceLine: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.body,
        paddingTop: spacing.xs,
    },
    consentEcho: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        fontStyle: 'italic',
        paddingTop: 4,
    },
}));

export default SignatureField;
